import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, GraduationCap, Stethoscope, FlaskConical, Home as HomeIcon, Briefcase,
  Users, BookOpen, Trophy, Presentation, Library, BedDouble, HeartPulse, Award, ShieldCheck, Sparkles,
  Play, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import InquiryForm from "@/components/InquiryForm";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const iconMap = {
  GraduationCap, Stethoscope, FlaskConical, Home: HomeIcon, Briefcase, Users, BookOpen, Trophy,
  Presentation, Library, BedDouble, ShieldCheck, Award, HeartPulse, Sparkles,
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Home() {
  const { settings } = useSettings();

  const hero = settings?.hero || {};
  const welcome = settings?.welcome || {};
  const stats = settings?.stats || [];
  const why = settings?.whyChoose || {};
  const campus = settings?.campusHighlights || {};
  const affiliations = settings?.affiliations || [];
  const brand = settings?.branding || {};
  const heroImages = (hero.images || []).map(assetUrl).filter(Boolean).slice(0, 4);
  const tileCount = heroImages.length;
  // Adaptive grid columns based on how many hero images admin uploaded
  const gridColsClass = tileCount <= 1 ? "grid-cols-1" : tileCount === 3 ? "grid-cols-3" : "grid-cols-2";

  return (
    <div data-testid="home-page">
      {/* HERO — cinematic editorial layout */}
      <section className="relative overflow-hidden">
        {/* Ambient background blobs */}
        <div aria-hidden className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl animate-blob"
          style={{ background: "radial-gradient(circle, var(--brand-primary), transparent 60%)" }} />
        <div aria-hidden className="absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full opacity-15 blur-3xl animate-blob"
          style={{ background: "radial-gradient(circle, var(--brand-secondary), transparent 60%)", animationDelay: "3s" }} />
        <div aria-hidden className="absolute inset-0 bg-blueprint opacity-70 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24 lg:pt-24 lg:pb-40 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6 lg:pr-4">
              {hero.eyebrow && (
                <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 shadow-sm pl-1 pr-4 py-1 mb-6">
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white" style={{ backgroundColor: "var(--brand-secondary)" }}>Live</span>
                  <span className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 brand-accent-text" /> {hero.eyebrow}
                  </span>
                </motion.div>
              )}

              <motion.h1
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.05 }}
                className="font-heading text-4xl sm:text-5xl lg:text-[68px] xl:text-7xl leading-[1.02] tracking-tight text-slate-900"
              >
                {hero.titleLeading}{" "}
                <span className="relative inline-block text-gradient-brand italic">
                  {hero.titleAccentPrimary}
                  <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none">
                    <path d="M2 8 Q 60 -2 100 5 T 198 5" fill="none" stroke="var(--brand-secondary)" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                </span>{" "}
                {hero.titleMiddle}{" "}
                <span style={{ color: "var(--brand-secondary)" }}>{hero.titleAccentSecondary}</span>{" "}
                {hero.titleTrailing}
              </motion.h1>

              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.12 }}
                className="text-slate-600 text-base sm:text-lg mt-7 max-w-xl leading-relaxed"
              >
                {hero.subtitle}
              </motion.p>

              <motion.div
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.2 }}
                className="mt-9 flex flex-wrap items-center gap-3"
              >
                <Link to={hero.ctaPrimaryLink || "/contact#inquiry"} data-testid="hero-apply-btn">
                  <Button
                    style={{ backgroundColor: "var(--brand-primary)" }}
                    className="h-12 rounded-full text-white px-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:brightness-110 transition-all"
                  >
                    {hero.ctaPrimaryText || "Apply Now"} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to={hero.ctaSecondaryLink || "/courses"} data-testid="hero-courses-btn">
                  <Button
                    variant="outline"
                    className="h-12 rounded-full border-2 px-7 hover:-translate-y-0.5 transition-transform bg-white/60 backdrop-blur"
                    style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
                  >
                    {hero.ctaSecondaryText || "Explore Courses"}
                  </Button>
                </Link>
              </motion.div>

              {/* Trust ribbon */}
              {hero.trustBadges && hero.trustBadges.length > 0 && (
                <motion.div
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.28 }}
                  className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-600"
                >
                  {hero.trustBadges.map((b) => {
                    const Icon = iconMap[b.icon] || ShieldCheck;
                    return (
                      <div key={b.label} className="flex items-center gap-2">
                        <Icon className="w-4 h-4 brand-accent-text" />
                        <span className="font-medium">{b.label}</span>
                      </div>
                    );
                  })}
                </motion.div>
              )}

              {/* Avatar cluster social proof — editable via Admin → Hero Section */}
              <motion.div
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.34 }}
                className="mt-8 flex items-center gap-4"
              >
                <div className="flex -space-x-3">
                  {(hero.socialProof?.images || []).slice(0, 4).map(assetUrl).map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt="Ambigara student"
                      className="w-10 h-10 rounded-full object-cover object-center ring-2 ring-white shadow bg-slate-200"
                    />
                  ))}
                  <span className="w-10 h-10 rounded-full grid place-items-center text-[10px] font-bold text-white ring-2 ring-white shadow"
                    style={{ backgroundColor: "var(--brand-primary)" }}>
                    +20K
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-900">{hero.socialProof?.label || "20,000+ students"}</span>
                  <br />{hero.socialProof?.caption || "shaping careers with Ambigara"}
                </p>
              </motion.div>
            </div>

            {/* Right — editorial image collage */}
            <div className="lg:col-span-6 mt-4 lg:mt-0">
              <div className="relative">
                {/* Decorative rotating ring — desktop only, positioned below the collage */}
                <div aria-hidden className="hidden lg:block pointer-events-none absolute -bottom-24 -right-8 w-56 h-56 opacity-15 z-0">
                  <div className="w-full h-full animate-slow-spin">
                    <svg viewBox="0 0 200 200" className="w-full h-full">
                      <defs>
                        <path id="circlePath" d="M 100, 100 m -80, 0 a 80,80 0 1,1 160,0 a 80,80 0 1,1 -160,0" />
                      </defs>
                      <text fill="var(--brand-primary)" fontSize="10" fontFamily="Manrope, sans-serif" letterSpacing="5">
                        <textPath href="#circlePath">
                          AMBIGARA • CHOWDAIAH • COLLEGE OF NURSING • BANGALORE • EST 2010 •
                        </textPath>
                      </text>
                    </svg>
                  </div>
                </div>

                {/* MOBILE / TABLET: adaptive equal-square grid */}
                <div className={`lg:hidden grid ${gridColsClass} gap-3 sm:gap-4`}>
                  {heroImages.map((src, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 * i }}
                      className="aspect-square rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_20px_40px_-15px_rgba(15,23,42,0.25)] relative bg-slate-200"
                    >
                      <img src={src} alt={`Ambigara moment ${i + 1}`} className="absolute inset-0 w-full h-full object-cover object-center" loading={i === 0 ? "eager" : "lazy"} />
                      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(15,23,42,0.32))" }} />
                      {i === 0 && (
                        <div className="absolute bottom-2 left-2 right-2 flex items-center text-white">
                          <div className="flex items-center gap-1.5 text-[10px] bg-white/10 backdrop-blur px-2 py-1 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* DESKTOP: adaptive equal-square grid (2×2, 3-col, or single) */}
                <div className={`hidden lg:grid ${gridColsClass} gap-4 relative z-10`}>
                  {heroImages.map((src, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 + i * 0.12 }}
                      className="aspect-square rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.28)] relative bg-slate-200 group"
                    >
                      <img
                        src={src}
                        alt={`Ambigara moment ${i + 1}`}
                        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[800ms] ease-out group-hover:scale-[1.05]"
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(15,23,42,0.32))" }} />
                      {i === 0 && (
                        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white">
                          <div className="flex items-center gap-2 text-xs bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live at Clinical Rotation
                          </div>
                          <button className="w-11 h-11 rounded-full bg-white text-slate-900 grid place-items-center hover:scale-105 transition-transform">
                            <Play className="w-4 h-4 ml-0.5" />
                          </button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Affiliations marquee */}
        {affiliations.length > 0 && (
          <div className="border-y border-slate-200 bg-white relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center gap-8 overflow-hidden">
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 shrink-0">
                Affiliated & Recognized
              </span>
              <div className="relative flex-1 overflow-hidden">
                <div className="flex items-center gap-14 animate-marquee whitespace-nowrap">
                  {[...affiliations, ...affiliations, ...affiliations].map((a, i) => (
                    <span key={i} className="text-sm font-medium text-slate-600 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 brand-accent-text" /> {a}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Stats strip */}
      {stats.length > 0 && (
        <section className="bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
            {stats.map((s, i) => (
              <motion.div key={`${s.label}-${i}`} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }} className="text-center md:text-left">
                <div className="font-heading text-4xl sm:text-5xl number-block" style={{ color: "var(--brand-primary)" }}>{s.value}</div>
                <div className="text-xs sm:text-sm text-slate-500 mt-1 uppercase tracking-wider font-medium">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Welcome / Intro */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{welcome.eyebrow || "Welcome"}</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              {welcome.heading}
            </h2>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-7 space-y-4">
            <p className="text-slate-600 text-lg leading-relaxed">{welcome.paragraph1}</p>
            <p className="text-slate-600 leading-relaxed">{welcome.paragraph2}</p>
          </motion.div>
        </div>
      </section>

      {/* Why choose us — bento */}
      {why.items && why.items.length > 0 && (
        <section className="py-20 lg:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-14">
              <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{why.eyebrow}</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">{why.heading}</h2>
              {why.intro && <p className="text-slate-600 mt-4 text-lg">{why.intro}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {why.items.map((w, i) => {
                const Icon = iconMap[w.icon] || GraduationCap;
                return (
                  <motion.div
                    key={w.title}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: i * 0.04 }}
                    data-testid={`why-card-${i}`}
                    className="group bg-white rounded-2xl p-6 sm:p-7 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div
                      className="w-12 h-12 rounded-xl grid place-items-center mb-5 transition-colors"
                      style={{ backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-heading text-lg text-slate-900 leading-snug">{w.title}</h3>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Campus highlights */}
      {campus.items && campus.items.length > 0 && (
        <section className="py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
              <div className="lg:col-span-7">
                <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{campus.eyebrow}</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">{campus.heading}</h2>
              </div>
              {campus.intro && <p className="lg:col-span-5 text-slate-600 text-lg">{campus.intro}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campus.items.map((c, i) => {
                const Icon = iconMap[c.icon] || Presentation;
                const dark = i === 0;
                return (
                  <motion.div
                    key={c.title}
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                    data-testid={`highlight-card-${i}`}
                    style={dark ? { backgroundColor: "var(--brand-primary)" } : {}}
                    className={`rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                      dark ? "text-white border-transparent shadow-[0_20px_40px_rgba(30,58,138,0.25)]"
                        : "bg-white border-slate-100 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
                    }`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl grid place-items-center mb-5"
                      style={dark ? { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" } : { backgroundColor: "rgba(244,63,94,0.08)", color: "var(--brand-secondary)" }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className={`font-heading text-xl leading-snug ${dark ? "text-white" : "text-slate-900"}`}>{c.title}</h3>
                    <p className={`text-sm mt-3 leading-relaxed ${dark ? "text-white/80" : "text-slate-600"}`}>{c.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial-style band */}
      <section className="py-20 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grain-dark opacity-30 pointer-events-none" />
        <div aria-hidden className="absolute -top-24 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20 animate-blob"
          style={{ background: "radial-gradient(circle, var(--brand-secondary), transparent 60%)" }} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.24em] text-white/70 mb-6">
            <Sparkles className="w-3.5 h-3.5 brand-accent-text" /> Words that inspire us
          </div>
          <p className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-snug italic">
            &ldquo;Healthcare is one of the most respected professions in the world. At Ambigara Chowdaiah College of Nursing we shape nurses who combine{" "}
            <span className="brand-accent-text">clinical expertise</span> with{" "}
            <span style={{ color: "var(--brand-secondary)" }}>compassion, discipline, and confidence</span>.&rdquo;
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/10 grid place-items-center font-heading text-sm">SM</div>
            <div className="text-left">
              <p className="font-heading text-base">Mrs. Sumitra Murmu</p>
              <p className="text-xs text-white/60">Principal, {brand.collegeName || "Ambigara Chowdaiah College of Nursing"}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry form section */}
      <section id="inquiry" className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Get Started</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Start Your Journey Towards a <span className="italic">Rewarding</span> Healthcare Career
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Applications are open for the upcoming academic session. Fill out the form and we&rsquo;ll get in touch with admission details.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              {[
                { Icon: HeartPulse, text: "Compassion-first curriculum", color: "var(--brand-secondary)" },
                { Icon: Stethoscope, text: "Real hospital exposure from year one", color: "var(--brand-primary)" },
                { Icon: Trophy, text: "Career mentorship & placement support", color: "var(--brand-accent)" },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-700">
                  <span className="w-8 h-8 rounded-full bg-white border border-slate-200 grid place-items-center" style={{ color: f.color }}>
                    <f.Icon className="w-4 h-4" />
                  </span>
                  {f.text}
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
              <ChevronRight className="w-3.5 h-3.5" /> Response within one working day
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-7">
            <InquiryForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
